import { useLayoutEffect } from 'react';
import type React from 'react';

import { withI18nPlaceholders } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/videoFirstHero/video-first-hero.yml';

import { VideoFirstHeroDesktop } from './components/VideoFirstHeroDesktop';
import { VideoFirstHeroMobile } from './components/VideoFirstHeroMobile';
import { useVideoFirstHero } from './hooks/useVideoFirstHero';
import type { VideoFirstViewport } from './hooks/useVideoFirstHero';

const VideoFirstHeroClient: React.FC<Props> = ({ fallback, onRenderChange, sourceUrl, viewport }): React.ReactNode => {
	const {
		data: { coverUrl, creatorName, playerUrl, shouldRender, signatureCount, signatureGoal, title, titleDataQa },
	} = useVideoFirstHero(sourceUrl, viewport);
	const rendersHero = shouldRender && !!playerUrl;

	useLayoutEffect(() => {
		if (!onRenderChange) return undefined;

		onRenderChange(rendersHero);
		return () => onRenderChange(false);
	}, [onRenderChange, rendersHero]);

	if (!shouldRender || !playerUrl) return fallback;

	if (viewport === 'desktop') {
		return (
			<VideoFirstHeroDesktop
				coverUrl={coverUrl}
				creatorName={creatorName}
				playerUrl={playerUrl}
				title={title}
				titleDataQa={titleDataQa}
			/>
		);
	}

	return (
		<VideoFirstHeroMobile
			coverUrl={coverUrl}
			creatorName={creatorName}
			playerUrl={playerUrl}
			signatureCount={signatureCount}
			signatureGoal={signatureGoal}
			title={title}
			titleDataQa={titleDataQa}
		/>
	);
};

const VideoFirstHeroComponent: React.FC<Props> = ({ fallback, onRenderChange, sourceUrl, viewport }) => {
	return (
		<ClientRender placeholder={fallback}>
			{() => (
				<VideoFirstHeroClient
					fallback={fallback}
					onRenderChange={onRenderChange}
					sourceUrl={sourceUrl}
					viewport={viewport}
				/>
			)}
		</ClientRender>
	);
};

export const VideoFirstHero = withI18nPlaceholders(I18N_PLACEHOLDERS, VideoFirstHeroComponent);

type Props = {
	fallback: React.ReactNode;
	onRenderChange?: (rendered: boolean) => void;
	sourceUrl: string;
	viewport: VideoFirstViewport;
};
